import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

import { BrandMark } from '@/components/BrandMark';
import { useT } from '@/i18n/locale';
import { pageVariants } from '@/lib/motion';

import { Marquee } from './Marquee';

/*
 * The threshold (web/docs/screens.md §"/login etc.", web/docs/ui-ux.md §11).
 *
 * Desktop (lg+): two halves. The left half is the door — brand row, then a
 * left-aligned form column (max 400px) vertically centred. The right half is
 * the marquee: a projection-room panel of slowly drifting covers that says
 * what the product is (covers-first) before a word is read. It is decorative
 * (aria-hidden) and static under reduced motion.
 *
 * Below lg the marquee is not rendered at all (Marquee checks the viewport),
 * so a phone visitor gets the form, nothing competing with it, and no poster
 * downloads.
 */
export function AuthLayout({
  title,
  lede,
  children,
}: {
  title: string;
  lede?: ReactNode;
  children: ReactNode;
}) {
  const t = useT();
  return (
    <div className="min-h-dvh bg-bg lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <main className="flex min-h-dvh flex-col px-5 py-6 sm:px-10 lg:px-14 xl:px-20">
        {/* Below lg the brand row shares the form's 400px column so the two
            left edges line up; at lg+ it sits at the page gutter. */}
        <div className="w-full max-lg:mx-auto max-lg:max-w-[400px]">
          <a
            href="/"
            aria-label={t('auth.homeLink')}
            className="inline-flex items-center gap-2.5 rounded"
          >
            <BrandMark size="md" />
          </a>
        </div>

        <div className="flex flex-1 items-center py-12 lg:py-16">
          <motion.div
            variants={pageVariants()}
            initial="initial"
            animate="animate"
            className="w-full max-w-[400px] max-lg:mx-auto"
          >
            <h1 className="text-display-sm text-ink">{title}</h1>
            {lede ? <div className="mt-3 text-body text-ink-secondary">{lede}</div> : null}
            <div className="mt-8">{children}</div>
          </motion.div>
        </div>
      </main>

      <Marquee />
    </div>
  );
}
